import { Plus, Smartphone } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  billing as billingApi,
  instances as instancesApi,
  projects as projectsApi,
} from '../api/resources.js';
import {
  Banner,
  Card,
  CopyButton,
  Empty,
  ErrorState,
  Loading,
  StatusPill,
} from '../components/ui.js';
import { Button } from '../components/ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog.js';
import { Field, Input, inputClass } from '../components/ui/input.js';
import { useToast } from '../components/toast.js';
import { daysUntil, formatPhone, formatRelative } from '../lib/format.js';
import { useAction, useAsync, useTicker } from '../lib/hooks.js';

/**
 * Lista e criação de instâncias (PRD §26).
 *
 * A criação vive num diálogo, não num formulário permanente no topo: quem entra aqui
 * dez vezes por dia vem olhar o estado das instâncias, e criar uma é a exceção.
 */
export function InstancesPage() {
  useTicker(10_000);
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [projectId, setProjectId] = useState('');

  const data = useAsync(async () => {
    const [instances, projects, subscription] = await Promise.all([
      instancesApi.list(),
      projectsApi.list(),
      // A assinatura não bloqueia a tela: se falhar, a lista aparece sem o aviso.
      billingApi.subscription().catch(() => null),
    ]);
    return { instances: instances.data, projects: projects.data, subscription };
  }, []);

  const effectiveProject = projectId || data.data?.projects[0]?.id || '';

  const create = useAction(async () => {
    const instance = await instancesApi.create(name, effectiveProject);
    toast.success('Instância criada', 'Escaneie o QR Code ou gere um código para conectar.');
    setName('');
    setOpen(false);
    data.reload();
    return instance;
  });

  if (data.loading) return <Loading />;
  if (data.error) return <ErrorState error={data.error} onRetry={data.reload} />;
  if (!data.data) return null;

  const createError = create.error as
    { code?: string; message?: string; details?: { plan?: string } } | undefined;

  const billingNotice = billingNoticeFor(data.data.subscription);

  const newInstanceButton = (
    <Button onClick={() => setOpen(true)}>
      <Plus /> Nova instância
    </Button>
  );

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="m-0 text-xl font-semibold">Instâncias</h2>
          <p className="m-0 text-sm text-muted-foreground">
            Cada instância conecta um número de WhatsApp.
          </p>
        </div>
        {newInstanceButton}
      </div>

      {billingNotice && (
        <Banner tone={billingNotice.tone}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <strong>{billingNotice.title}</strong> {billingNotice.body}
            </div>
            <Button
              size="sm"
              variant={billingNotice.tone === 'danger' ? 'default' : 'outline'}
              asChild
            >
              <Link to="/billing" className="no-underline">
                {billingNotice.cta}
              </Link>
            </Button>
          </div>
        </Banner>
      )}

      <Card bodyless>
        {data.data.instances.length === 0 ? (
          <Empty title="Nenhuma instância" icon={<Smartphone />}>
            <p className="muted m-0">Crie a primeira para conectar um número.</p>
            <div className="mt-3">{newInstanceButton}</div>
          </Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Número</th>
                  <th>Status</th>
                  <th>Última atividade</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {data.data.instances.map((instance) => (
                  <tr key={instance.id}>
                    <td>
                      <Link to={`/instances/${instance.id}`} className="font-medium">
                        {instance.name}
                      </Link>
                      <div className="flex items-center gap-1">
                        <span className="subtle mono">{instance.id}</span>
                        <CopyButton
                          value={instance.id}
                          label="Copiar ID da instância"
                          variant="ghost"
                          size="icon-xs"
                        />
                      </div>
                    </td>
                    <td>
                      <div>{formatPhone(instance.phoneNumber)}</div>
                      {instance.profileName && <div className="subtle">{instance.profileName}</div>}
                    </td>
                    <td>
                      <StatusPill status={instance.status} />
                    </td>
                    <td className="subtle">{formatRelative(instance.lastSeenAt)}</td>
                    <td className="text-right">
                      <Button variant="outline" size="sm" asChild>
                        <Link
                          to={`/instances/${instance.id}`}
                          className="text-foreground no-underline"
                        >
                          Gerenciar
                        </Link>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void create.run();
            }}
          >
            <DialogHeader>
              <DialogTitle>Nova instância</DialogTitle>
              <DialogDescription>
                Depois de criada, conecte um número pelo QR Code ou por código de pareamento.
              </DialogDescription>
            </DialogHeader>

            <div className="my-4">
              <Field label="Nome" htmlFor="instance-name">
                <Input
                  id="instance-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Atendimento Comercial"
                  required
                  minLength={2}
                  autoFocus
                />
              </Field>
              <Field label="Projeto" htmlFor="instance-project">
                <select
                  id="instance-project"
                  className={inputClass}
                  value={effectiveProject}
                  onChange={(event) => setProjectId(event.target.value)}
                >
                  {data.data.projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name} ({project.environment})
                    </option>
                  ))}
                </select>
              </Field>

              {createError && (
                <Banner tone={createError.code === 'INSTANCE_LIMIT_REACHED' ? 'warn' : 'danger'}>
                  <div>
                    <strong>{createError.message}</strong>
                    {createError.code === 'INSTANCE_LIMIT_REACHED' && (
                      <>
                        {' '}
                        <Link to="/billing">Ver planos</Link>
                      </>
                    )}
                  </div>
                </Banner>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={create.pending}>
                {create.pending ? 'Criando…' : 'Criar instância'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * O aviso de cobrança que a lista de instâncias mostra (PRD §129): quem está prestes a
 * perder o envio — ou já perdeu — descobre aqui, onde vai olhar primeiro, e não só na
 * tela de cobrança.
 */
function billingNoticeFor(
  subscription: { status?: string; trialEnd?: string | null; pendingPayment?: unknown } | null,
): { tone: 'info' | 'warn' | 'danger'; title: string; body: string; cta: string } | null {
  if (!subscription?.status) return null;
  if (subscription.pendingPayment) {
    return {
      tone: 'warn',
      title: 'Há uma cobrança aguardando pagamento.',
      body: 'Conclua o pagamento para ativar o plano contratado.',
      cta: 'Continuar pagamento',
    };
  }
  switch (subscription.status) {
    case 'RESTRICTED':
    case 'SUSPENDED':
      return {
        tone: 'danger',
        title: 'O envio de mensagens está bloqueado.',
        body:
          subscription.status === 'SUSPENDED'
            ? 'As sessões foram encerradas por falta de pagamento; contrate um plano para reativá-las. Nada foi apagado.'
            : 'Contrate um plano para voltar a enviar. Seus dados continuam acessíveis.',
        cta: 'Contratar plano',
      };
    case 'PAST_DUE':
    case 'GRACE_PERIOD':
      return {
        tone: 'warn',
        title: 'Há uma fatura em atraso.',
        body: 'Regularize para não ter o envio de mensagens restringido.',
        cta: 'Ver cobrança',
      };
    case 'TRIALING': {
      const days = daysUntil(subscription.trialEnd);
      if (days === null || days > 3) return null;
      return {
        tone: 'warn',
        title:
          days > 0
            ? `Seu período de teste termina em ${days} dia${days === 1 ? '' : 's'}.`
            : 'Seu período de teste terminou.',
        body: 'Contrate um plano para continuar conectando números.',
        cta: 'Contratar plano',
      };
    }
    default:
      return null;
  }
}
